import React, {useState, useMemo} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SourceItemDrawer from '../SourceItemDrawer';

interface CaseResult {
  testCaseId: string;
  sourceItemTitle: string;
  pass: boolean;
  expected: {guideable: boolean; topic: string; audience: string};
  actual: {guideable: boolean; topic: string; audience: string};
  diffs: string[];
}

interface TestCaseRef {
  id: string;
  sourceItemId: string;
}

interface EvalResultsTableProps {
  results: CaseResult[];
  /** Test cases list — used to map testCaseId → sourceItemId for the document drawer */
  testCases?: TestCaseRef[];
  /** Called to rerun a single test case. If provided, shows rerun buttons per row. */
  onRerunCase?: (testCaseId: string) => Promise<void>;
}

function FieldCheck({expected, actual}: {expected: any; actual: any}) {
  const match = expected === actual || String(expected) === String(actual);
  return match
    ? <CheckCircleIcon sx={{fontSize: 16, color: '#10B981'}} />
    : <CancelIcon sx={{fontSize: 16, color: '#EF4444'}} />;
}

export default function EvalResultsTable({results, testCases, onRerunCase}: EvalResultsTableProps) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [drawerItemId, setDrawerItemId] = useState<string | null>(null);
  const [rerunningCase, setRerunningCase] = useState<string | null>(null);

  const hasActions = (testCases && testCases.length > 0) || !!onRerunCase;
  const colCount = 5 + (hasActions ? 1 : 0);

  // Build a map from testCaseId → sourceItemId for quick lookup
  const testCaseMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const tc of testCases || []) {
      map.set(tc.id, tc.sourceItemId);
    }
    return map;
  }, [testCases]);

  const handleOpenDrawer = (e: React.MouseEvent, testCaseId: string) => {
    e.stopPropagation();
    const sourceItemId = testCaseMap.get(testCaseId);
    if (sourceItemId) setDrawerItemId(sourceItemId);
  };

  const handleRerunCase = async (e: React.MouseEvent, testCaseId: string) => {
    e.stopPropagation();
    if (!onRerunCase || rerunningCase) return;
    setRerunningCase(testCaseId);
    try {
      await onRerunCase(testCaseId);
    } finally {
      setRerunningCase(null);
    }
  };

  return (
    <>
      <TableContainer sx={{bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB'}}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{bgcolor: '#F9FAFB'}}>
              <TableCell sx={{fontSize: 12, fontWeight: 600, color: '#6B7280'}}>Title</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 60}}>Pass</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 60}}>Guide</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 60}}>Topic</TableCell>
              <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 60}}>Aud.</TableCell>
              {hasActions && (
                <TableCell align="center" sx={{fontSize: 12, fontWeight: 600, color: '#6B7280', width: 72}} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((r) => {
              const hasSourceItem = testCaseMap.has(r.testCaseId);
              const isRerunning = rerunningCase === r.testCaseId;
              return (
                <React.Fragment key={r.testCaseId}>
                  <TableRow
                    hover
                    onClick={() => setExpandedRow(expandedRow === r.testCaseId ? null : r.testCaseId)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: r.pass ? 'transparent' : '#FFFBFB',
                      '&:hover': {bgcolor: r.pass ? '#F9FAFB' : '#FEF2F2'},
                    }}
                  >
                    <TableCell sx={{fontSize: 13, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {r.sourceItemTitle}
                    </TableCell>
                    <TableCell align="center">
                      {r.pass
                        ? <CheckCircleIcon sx={{fontSize: 16, color: '#10B981'}} />
                        : <CancelIcon sx={{fontSize: 16, color: '#EF4444'}} />
                      }
                    </TableCell>
                    <TableCell align="center"><FieldCheck expected={r.expected.guideable} actual={r.actual.guideable} /></TableCell>
                    <TableCell align="center"><FieldCheck expected={r.expected.topic} actual={r.actual.topic} /></TableCell>
                    <TableCell align="center"><FieldCheck expected={r.expected.audience} actual={r.actual.audience} /></TableCell>
                    {hasActions && (
                      <TableCell align="center" sx={{px: 0.5, whiteSpace: 'nowrap'}}>
                        {onRerunCase && (
                          <Tooltip title="Rerun this case">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => handleRerunCase(e, r.testCaseId)}
                                disabled={!!rerunningCase}
                                sx={{color: '#9CA3AF', '&:hover': {color: '#F59E0B'}}}
                              >
                                {isRerunning ? <CircularProgress size={14} /> : <ReplayIcon sx={{fontSize: 16}} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {hasSourceItem && (
                          <Tooltip title="View source document">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenDrawer(e, r.testCaseId)}
                              sx={{color: '#9CA3AF', '&:hover': {color: '#3B82F6'}}}
                            >
                              <DescriptionOutlinedIcon sx={{fontSize: 16}} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={colCount} sx={{py: 0, borderBottom: expandedRow === r.testCaseId ? undefined : 'none'}}>
                      <Collapse in={expandedRow === r.testCaseId}>
                        <Box sx={{py: 1.5, px: 1}}>
                          {r.diffs.length > 0 ? (
                            r.diffs.map((d, i) => (
                              <Typography key={i} sx={{fontSize: 12, color: '#991B1B', fontFamily: 'monospace', mb: 0.5}}>
                                {d}
                              </Typography>
                            ))
                          ) : (
                            <Typography sx={{fontSize: 12, color: '#065F46'}}>All fields match.</Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Source item detail drawer */}
      <SourceItemDrawer
        open={!!drawerItemId}
        sourceItemId={drawerItemId}
        onClose={() => setDrawerItemId(null)}
      />
    </>
  );
}
